// =============================================================================
// ANCHOR IDL TYPES - Auto-generated from Anchor programs
// These types will be used once Anchor programs are deployed
// =============================================================================
import { PublicKey, Connection } from "@solana/web3.js";
import { Buffer } from "buffer";
import MoonrunTokenIDL from "../../idl/moonrun_token.json";
import MoonrunBondingCurveIDL from "../../idl/moonrun_bonding_curve.json";
import MoonrunGovernanceIDL from "../../idl/moonrun_governance.json";


// Program IDs - strict on-chain enforcement, no fallbacks to placeholders
export const PROGRAM_IDS = {
  TOKEN: import.meta.env.VITE_MOONRUN_TOKEN_PROGRAM_ID,
  BONDING_CURVE: import.meta.env.VITE_MOONRUN_BONDING_CURVE_PROGRAM_ID,
  GOVERNANCE: import.meta.env.VITE_MOONRUN_GOVERNANCE_PROGRAM_ID,
} as const;

// Validation functions for strict program ID enforcement
export async function validateProgramId(programId: string | undefined, programName: string, connection: Connection): Promise<void> {
  if (!programId) {
    throw new Error(`${programName} program ID not configured - strict on-chain enforcement required`);
  }

  try {
    const pubkey = new PublicKey(programId);
    const accountInfo = await connection.getAccountInfo(pubkey);

    if (!accountInfo) {
      throw new Error(`${programName} program not deployed on-chain at ${programId}`);
    }

    if (!accountInfo.executable) {
      throw new Error(`${programName} program at ${programId} is not executable`);
    }

    console.log(`${programName} program validated: ${programId}`);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to validate ${programName} program: ${String(error)}`);
  }
}

export async function validateAllPrograms(connection: Connection): Promise<void> {
  await validateProgramId(PROGRAM_IDS.TOKEN, "Token", connection);
  await validateProgramId(PROGRAM_IDS.BONDING_CURVE, "Bonding Curve", connection);
  await validateProgramId(PROGRAM_IDS.GOVERNANCE, "Governance", connection);
}

// =============================================================================
// TOKEN PROGRAM TYPES
// =============================================================================

export interface TokenConfig {
  mint: PublicKey;
  creator: PublicKey;
  name: string;
  symbol: string;
  uri: string;
  totalSupply: bigint;
  decimals: number;
  createdAt: bigint;
  isFrozen: boolean;
  bump: number;
}

export interface CreateTokenArgs {
  name: string;
  symbol: string;
  uri: string;
  decimals: number;
  totalSupply: bigint;
}

// =============================================================================
// BONDING CURVE PROGRAM TYPES
// =============================================================================

export interface BondingCurvePool {
  tokenMint: PublicKey;
  solReserve: bigint;
  tokenReserve: bigint;
  kConstant: bigint;
  totalVolume: bigint;
  marketCap: bigint;
  targetMarketCap: bigint;
  isLocked: boolean;
  isMigrated: boolean;
  creator: PublicKey;
  feeBasisPoints: number;
  createdAt: bigint;
  bump: number;
}

export interface SellQueue {
  pool: PublicKey;
  orders: SellOrder[];
  bump: number;
}

export interface SellOrder {
  seller: PublicKey;
  amount: bigint;
  minSolOut: bigint;
  timestamp: bigint;
  isPriority: boolean;
}

export interface InitializePoolArgs {
  targetMarketCap: bigint;
}

export interface BuyArgs {
  solAmount: bigint;
  minTokensOut: bigint;
}

export interface SellArgs {
  tokenAmount: bigint;
  minSolOut: bigint;
}

export interface QueueSellArgs {
  tokenAmount: bigint;
  minSolOut: bigint;
  isPriority: boolean;
}

// =============================================================================
// GOVERNANCE PROGRAM TYPES
// =============================================================================

export interface GovernanceConfig {
  tokenMint: PublicKey;
  pool: PublicKey;
  votesToLock: bigint;
  votesToUnlock: bigint;
  totalUserWeight: bigint;
  totalStreamerWeight: bigint;
  lastVoteTimestamp: bigint;
  voteCooldown: bigint;
  isActive: boolean;
  bump: number;
}

export interface Vote {
  voter: PublicKey;
  governance: PublicKey;
  direction: boolean; // true = unlock, false = lock
  weight: bigint;
  isStreamer: boolean;
  timestamp: bigint;
}

export interface VoterProfile {
  wallet: PublicKey;
  isStreamer: boolean;
  isVerified: boolean;
  totalHoldingsValue: bigint;
  votePower: bigint;
}

export interface CastVoteArgs {
  direction: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

export const BONDING_CURVE_CONSTANTS = {
  DEFAULT_TARGET_MARKET_CAP: 69_000n * 1_000_000_000n, // 69,000 SOL in lamports
  FEE_BASIS_POINTS: 100, // 1%
  INITIAL_TOKEN_SUPPLY: 1_000_000_000n * 1_000_000_000n, // 1B with 9 decimals
  USER_VOTE_WEIGHT: 75,
  STREAMER_VOTE_WEIGHT: 25,
  VERIFIED_STREAMER_MULTIPLIER: 150, // 1.5x
  UNVERIFIED_STREAMER_MULTIPLIER: 125, // 1.25x
  VOTE_COOLDOWN_SECONDS: 3600, // 1 hour
} as const;

// =============================================================================
// PDA SEEDS
// =============================================================================

export const PDA_SEEDS = {
  TOKEN_CONFIG: Buffer.from("token_config"),
  POOL: Buffer.from("pool"),
  SELL_QUEUE: Buffer.from("sell_queue"),
  POOL_SOL_VAULT: Buffer.from("pool_sol_vault"),
  POOL_TOKEN_VAULT: Buffer.from("pool_token_vault"),
  GOVERNANCE: Buffer.from("governance"),
  VOTE: Buffer.from("vote"),
  VOTER_PROFILE: Buffer.from("voter_profile"),
} as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function findTokenConfigPDA(mint: PublicKey, programId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PDA_SEEDS.TOKEN_CONFIG, mint.toBuffer()],
    programId
  );
}

export function findPoolPDA(tokenMint: PublicKey, programId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PDA_SEEDS.POOL, tokenMint.toBuffer()],
    programId
  );
}

export function findSellQueuePDA(pool: PublicKey, programId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PDA_SEEDS.SELL_QUEUE, pool.toBuffer()],
    programId
  );
}

export function findGovernancePDA(tokenMint: PublicKey, programId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PDA_SEEDS.GOVERNANCE, tokenMint.toBuffer()],
    programId
  );
}

export function findVotePDA(governance: PublicKey, voter: PublicKey, programId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PDA_SEEDS.VOTE, governance.toBuffer(), voter.toBuffer()],
    programId
  );
}

export function findVoterProfilePDA(wallet: PublicKey, programId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PDA_SEEDS.VOTER_PROFILE, wallet.toBuffer()],
    programId
  );
}

export function findPoolTokenAccountPDA(pool: PublicKey, programId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PDA_SEEDS.POOL_TOKEN_VAULT, pool.toBuffer()],
    programId
  );
}

export function findPoolSolAccountPDA(pool: PublicKey, programId: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PDA_SEEDS.POOL_SOL_VAULT, pool.toBuffer()],
    programId
  );
}

// =============================================================================
// CALCULATION HELPERS (mirrors on-chain logic)
// =============================================================================

export function calculateBuyAmount(
  solIn: bigint,
  solReserve: bigint,
  tokenReserve: bigint
): bigint {
  const k = solReserve * tokenReserve;
  const newSolReserve = solReserve + solIn;
  const newTokenReserve = k / newSolReserve;
  return tokenReserve - newTokenReserve;
}

export function calculateSellAmount(
  tokensIn: bigint,
  solReserve: bigint,
  tokenReserve: bigint
): bigint {
  const k = solReserve * tokenReserve;
  const newTokenReserve = tokenReserve + tokensIn;
  const newSolReserve = k / newTokenReserve;
  return solReserve - newSolReserve;
}

export function calculateVoteWeight(
  tokenHoldings: bigint,
  isStreamer: boolean,
  isVerified: boolean
): bigint {
  const baseWeight = tokenHoldings;
  let multiplier = 100n;

  if (isStreamer) {
    multiplier = isVerified
      ? BigInt(BONDING_CURVE_CONSTANTS.VERIFIED_STREAMER_MULTIPLIER)
      : BigInt(BONDING_CURVE_CONSTANTS.UNVERIFIED_STREAMER_MULTIPLIER);
  }

  return (baseWeight * multiplier) / 100n;
}

export function tallyVotes(
  userVotesLock: bigint,
  userVotesUnlock: bigint,
  streamerVotesLock: bigint,
  streamerVotesUnlock: bigint
): boolean {
  const userResult = userVotesUnlock > userVotesLock;
  const streamerResult = streamerVotesUnlock > streamerVotesLock;

  const weightedUnlock =
    (userResult ? BigInt(BONDING_CURVE_CONSTANTS.USER_VOTE_WEIGHT) : 0n) +
    (streamerResult ? BigInt(BONDING_CURVE_CONSTANTS.STREAMER_VOTE_WEIGHT) : 0n);

  return weightedUnlock > 50n;
}
